/**
 * Loading a study's DICOM images for the viewer without letting one failed
 * image stop the others.
 *
 * The image loader (cornerstoneWADOImageLoader 3.3.1, src/assets) rejects a
 * download that failed with `{ error: <the XMLHttpRequest> }`: an HTTP error,
 * no response at all (status 0) or an abort. An image it downloaded but could
 * not decode rejects with `{ error: <Error>, dataSet }`. cornerstone drops a
 * failed load from its image cache, and the loader drops it from its requests
 * in flight, so loading the same image again sends a new request.
 *
 * Seen at Ottawa on 2026-10-06: on two study pages one image failed (the load
 * balancer's 502 for one, no response for the other) and both files downloaded
 * on the next attempt. The viewer loaded the images with `Promise.all`, so it
 * showed none of them, and the loader's rejection reached the issue tracker as
 * a ThrownValue holding the request object.
 *
 * Seen at Ottawa on 2026-10-23: a user opened and deleted about twenty large
 * studies (up to 1,240 images each) within minutes. The viewer asked for all
 * of a study's images at once and went on downloading after its page was left,
 * so the downloads of the pages left behind piled up. Chrome fails a tab's
 * requests beyond about 1,350 in flight with status 0, without sending them:
 * 1,381 images failed that way, and so did the app's own queries for the
 * Studies list. So the viewer now has at most MAX_DOWNLOADS images in flight,
 * and asks for none once its page is left.
 *
 * Known gap, left alone: the loader's decode step runs inside a `then` with
 * nothing catching a throw, so a file that parses but whose first frame it
 * cannot decode (pixel data truncated, or an encoding it rejects on the spot)
 * never settles its load, and the viewer stays on "Downloading Images". Master
 * has the same gap. The issue tracker cannot tell whether it happens: a load
 * that never settles reports nothing. Files arrive whole from the gateways, so
 * it should be rare. A fix would watch the loader's progress events and give
 * up on an image only when no byte has arrived for a while; a fixed deadline
 * per image would cut off slow downloads that would have finished.
 */

/** how one image failed, for the report */
export interface ImageFailure {
  /** the file's name, `HI_PETVCT__1.2.840…_.dcm` */
  file: string
  /** HTTP status of a failed download; 0 = no response; absent when the download is not what failed */
  status?: number
  /** what went wrong, as a line */
  reason: string
}

/** at most this many failures are named in a report's message */
const MAX_NAMED = 3

/** the download is what failed: an HTTP error status, or no response (0) */
const isDownloadFailure = (failure: ImageFailure): boolean =>
  failure.status !== undefined

/** the server answered and refused: it does not have the file (404), and a reload will not change that */
const isRefusal = (failure: ImageFailure): boolean =>
  failure.status !== undefined && failure.status >= 400 && failure.status < 500

/**
 * Some of a study's images could not be loaded. Reported, never thrown. The
 * issue tracker groups a handled failure by the class of its cause, so each
 * kind of failure is its own class: a load balancer's 502 and a file that
 * cannot be read are different follow-ups.
 */
export abstract class ImageLoadError extends Error {
  constructor(
    readonly failures: ImageFailure[],
    /** how many images the study has */
    readonly total: number,
    name: string,
    /** "downloaded" or "read": what the images could not be */
    verb: string,
  ) {
    super(describeFailures(failures, total, verb))
    this.name = name
  }
}

/** downloads that failed: an HTTP error status (named in the message), or no response */
export class ImageDownloadError extends ImageLoadError {
  constructor(failures: ImageFailure[], total: number) {
    super(failures, total, 'ImageDownloadError', 'downloaded')
  }
}

/** files that downloaded but the loader could not read */
export class ImageDecodeError extends ImageLoadError {
  constructor(failures: ImageFailure[], total: number) {
    super(failures, total, 'ImageDecodeError', 'read')
  }
}

/** one error per kind of failure that happened, for one report each; none when nothing failed */
export function imageLoadErrors(
  failures: ImageFailure[],
  total: number,
): ImageLoadError[] {
  const downloads = failures.filter(isDownloadFailure)
  const decodes = failures.filter((failure) => !isDownloadFailure(failure))
  const errors: ImageLoadError[] = []
  if (downloads.length > 0) {
    errors.push(new ImageDownloadError(downloads, total))
  }
  if (decodes.length > 0) {
    errors.push(new ImageDecodeError(decodes, total))
  }
  return errors
}

/**
 * The note shown above the viewer, empty when every image loaded. An image
 * that could not be downloaded may come on a reload. One the server does not
 * have (a 4xx), and one that downloaded but could not be read (no pixel data,
 * an encoding the loader cannot decode, a file that is not DICOM), will not,
 * so those are not told to reload.
 */
export function failureNote(failures: ImageFailure[], total: number): string {
  const notFound = failures.filter(isRefusal).length
  const notDownloaded = failures.filter(isDownloadFailure).length - notFound
  const notRead = failures.length - notDownloaded - notFound
  const sentences: string[] = []
  if (notDownloaded > 0) {
    sentences.push(
      `${notDownloaded} of ${total} images could not be downloaded. Reload the page to try again.`,
    )
  }
  if (notFound > 0) {
    sentences.push(
      `${notFound} of ${total} images could not be found on the server.`,
    )
  }
  if (notRead > 0) {
    sentences.push(`${notRead} of ${total} images could not be read.`)
  }
  return sentences.join(' ')
}

/** a pause long enough for a dropped connection or a busy proxy to clear */
export const RETRY_DELAY_MS = 1000

/**
 * Whether a second try can help: a download that got no response (status 0)
 * or a server-side error (5xx, the load balancer's 502 among them) may work
 * next time. One the server refused (4xx: it does not have the file) and a
 * file that cannot be read would fail the same way again, so they are not
 * downloaded twice.
 */
export function worthRetrying(reason: unknown): boolean {
  const request = requestOf(reason)
  return (
    request !== undefined && (request.status === 0 || request.status >= 500)
  )
}

/**
 * `load()`, and once more after `delayMs` if it fails in a way a second try
 * can fix, unless `stopped()` by then. Rejects with the last failure.
 */
export async function loadWithRetry<T>(
  load: () => Promise<T>,
  delayMs = RETRY_DELAY_MS,
  stopped: () => boolean = () => false,
): Promise<T> {
  try {
    return await load()
  } catch (first) {
    if (!worthRetrying(first)) {
      throw first
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs))
    if (stopped()) {
      throw first
    }
    return load()
  }
}

/**
 * How many of a study's images download at once. Measured against demo on
 * 2026-10-23 (a study of 104 images, four tries each): with 32 the study page
 * showed its images as soon as with all of them at once (3.5 s), with 16 it
 * took 0.4 s longer, and 48 was no faster than 32. Ottawa's images came at 25
 * to 40 a second that day with 128 in flight (the most the load balancer runs
 * at once on one connection), each waiting 1 to 5 s for the server to start
 * answering; 32 in flight keep up with that. The rest wait in the viewer,
 * which can drop them, instead of in the browser.
 */
export const MAX_DOWNLOADS = 32

/**
 * `loadOne(item)` for every item, in order and at most `limit` at a time;
 * resolves when each has loaded or failed, with the failures. One failure does
 * not stop the others. Once `stopped()`, no more items are started: those
 * under way finish, and the others are never loaded and are not failures.
 */
export async function loadEach<T>(
  items: T[],
  loadOne: (item: T) => Promise<void>,
  urlOf: (item: T) => string,
  stopped: () => boolean = () => false,
  limit = MAX_DOWNLOADS,
): Promise<ImageFailure[]> {
  const failures: ImageFailure[] = []
  let next = 0
  // each takes the next item when its own has settled
  const worker = async (): Promise<void> => {
    while (next < items.length && !stopped()) {
      const item = items[next++]
      try {
        await loadOne(item)
      } catch (reason) {
        let url: string
        try {
          url = urlOf(item)
        } catch (e) {
          // the same lookup may be what failed (an image without a stored file)
          url = 'unknown file'
        }
        failures.push(describeImageFailure(reason, url))
      }
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  )
  return failures
}

const isObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null

/** what a loader rejection carries (`{ error, dataSet? }`), or the value itself */
const carriedBy = (reason: unknown): unknown =>
  isObject(reason) && 'error' in reason ? reason.error : reason

/** `HI_PETVCT__…_.dcm` from `wadouri:https://ottawa.myaqs.com/files/HI_PETVCT__…_.dcm` */
export function fileName(url: string): string {
  return url.split(/[?#]/)[0].split('/').pop() || url
}

/**
 * The request a loader rejection is or carries, when the download is what
 * failed (duck-typed, so specs can pass a plain object).
 */
function requestOf(
  reason: unknown,
): { status: number; statusText: string } | undefined {
  const carried = carriedBy(reason)
  if (
    isObject(carried) &&
    typeof carried.readyState === 'number' &&
    typeof carried.status === 'number'
  ) {
    return {
      status: carried.status,
      statusText:
        typeof carried.statusText === 'string' ? carried.statusText : '',
    }
  }
  return undefined
}

/** What a rejection of the image loader (or a failed parse) says went wrong with `url`. */
export function describeImageFailure(
  reason: unknown,
  url: string,
): ImageFailure {
  const file = fileName(url)
  const request = requestOf(reason)
  if (request) {
    const statusText = request.statusText ? ` ${request.statusText}` : ''
    return {
      file,
      status: request.status,
      reason:
        request.status === 0
          ? 'no response (HTTP status 0)'
          : `HTTP ${request.status}${statusText}`,
    }
  }
  const carried = carriedBy(reason)
  if (carried instanceof Error) {
    return { file, reason: `${carried.name}: ${carried.message}` }
  }
  return {
    file,
    reason: typeof carried === 'string' ? carried : 'unexpected failure',
  }
}

function describeFailures(
  failures: ImageFailure[],
  total: number,
  verb: string,
): string {
  const named = failures
    .slice(0, MAX_NAMED)
    .map((failure) => `${failure.file}: ${failure.reason}`)
    .join('; ')
  const more =
    failures.length > MAX_NAMED
      ? `; and ${failures.length - MAX_NAMED} more`
      : ''
  return `${failures.length} of ${total} images could not be ${verb}: ${named}${more}`
}
